import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PublicHeader from '../../components/ui/PublicHeader';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: '',
  });
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [passwordStrength, setPasswordStrength] = useState({ score: 0, text: '', color: '' });

  // Check if user has valid recovery session
  useEffect(() => {
    const checkRecoverySession = async () => {
      try {
        const { data: { session }, error } = await supabase?.auth?.getSession();
        
        if (error || !session) {
          setMessage({
            type: 'error',
            text: 'Invalid or expired password reset link. Please request a new one.',
          });
        }
      } catch (error) {
        console.error('Session check error:', error);
        setMessage({
          type: 'error',
          text: 'Failed to verify reset session. Please try again.',
        });
      }
    };

    checkRecoverySession();
  }, []);

  // Calculate password strength
  useEffect(() => {
    if (!formData?.newPassword) {
      setPasswordStrength({ score: 0, text: '', color: '' });
      return;
    }

    const password = formData?.newPassword;
    let score = 0;
    let text = '';
    let color = '';

    // Length check
    if (password?.length >= 8) score += 1;
    if (password?.length >= 12) score += 1;

    // Complexity checks
    if (/[a-z]/?.test(password)) score += 1;
    if (/[A-Z]/?.test(password)) score += 1;
    if (/[0-9]/?.test(password)) score += 1;
    if (/[^A-Za-z0-9]/?.test(password)) score += 1;

    // Determine strength text and color
    if (score <= 2) {
      text = 'Weak';
      color = 'text-error';
    } else if (score <= 4) {
      text = 'Moderate';
      color = 'text-warning';
    } else {
      text = 'Strong';
      color = 'text-success';
    }

    setPasswordStrength({ score, text, color });
  }, [formData?.newPassword]);

  const handleInputChange = (e) => {
    const { name, value } = e?.target || {};
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear messages when user starts typing
    if (message?.text) {
      setMessage({ type: '', text: '' });
    }
  };

  const validatePasswords = () => {
    const { newPassword, confirmPassword } = formData;

    if (!newPassword || !confirmPassword) {
      setMessage({
        type: 'error',
        text: 'Please fill in all password fields.',
      });
      return false;
    }

    if (newPassword?.length < 8) {
      setMessage({
        type: 'error',
        text: 'Password must be at least 8 characters long.',
      });
      return false;
    }

    if (newPassword !== confirmPassword) {
      setMessage({
        type: 'error',
        text: 'Passwords do not match. Please try again.',
      });
      return false;
    }

    if (passwordStrength?.score <= 2) {
      setMessage({
        type: 'error',
        text: 'Please choose a stronger password with a mix of uppercase, lowercase, numbers, and special characters.',
      });
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validatePasswords()) {
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const { data, error } = await supabase?.auth?.updateUser({
        password: formData?.newPassword,
      });

      if (error) {
        console.error('Password reset error:', error);
        setMessage({
          type: 'error',
          text: error?.message || 'Failed to reset password. Please try again.',
        });
        setLoading(false);
        return;
      }

      if (data) {
        setMessage({
          type: 'success',
          text: 'Password successfully updated! Redirecting to login...',
        });

        // Clear form
        setFormData({
          newPassword: '',
          confirmPassword: '',
        });

        // Redirect to login after 2 seconds
        setTimeout(() => {
          navigate('/admin-login');
        }, 2000);
      }
    } catch (error) {
      console.error('Unexpected error during password reset:', error);
      setMessage({
        type: 'error',
        text: 'An unexpected error occurred. Please try again.',
      });
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/admin-login');
  };

  return (
    <div className="min-h-screen bg-background">
      <PublicHeader />
      
      <main className="flex items-center justify-center px-4 py-16 sm:px-6 lg:px-8 mt-[60px]">
        <div className="w-full max-w-md">
          <div className="bg-card border border-border rounded-lg shadow-elevation-md p-8">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="flex justify-center mb-4">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                  <Icon name="Lock" size={24} className="text-primary" />
                </div>
              </div>
              <h1 className="text-2xl font-heading font-bold text-foreground mb-2">
                Reset Your Password
              </h1>
              <p className="text-sm text-muted-foreground">
                Enter your new password below to secure your account
              </p>
            </div>

            {/* Message Display */}
            {message?.text && (
              <div
                className={`mb-6 p-4 rounded-lg border ${
                  message?.type === 'success' ?'bg-success/10 border-success text-success' :'bg-error/10 border-error text-error'
                }`}
              >
                <div className="flex items-start gap-3">
                  <Icon
                    name={message?.type === 'success' ? 'CheckCircle' : 'AlertCircle'}
                    size={20}
                    className="flex-shrink-0 mt-0.5"
                  />
                  <p className="text-sm font-medium">{message?.text}</p>
                </div>
              </div>
            )}

            {/* Password Reset Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* New Password Field */}
              <div>
                <div className="relative">
                  <Input
                    type={showNewPassword ? 'text' : 'password'}
                    name="newPassword"
                    id="newPassword"
                    value={formData?.newPassword}
                    onChange={handleInputChange}
                    placeholder="Enter new password"
                    label="New Password"
                    required
                    disabled={loading || message?.type === 'success'}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-[38px] text-muted-foreground hover:text-foreground transition-colors"
                    disabled={loading || message?.type === 'success'}
                    aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                  >
                    <Icon name={showNewPassword ? 'EyeOff' : 'Eye'} size={18} />
                  </button>
                </div>
                
                {/* Password Strength Indicator */}
                {formData?.newPassword && (
                  <div className="mt-2">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                        <div
                          className={`h-full transition-all duration-300 ${
                            passwordStrength?.score <= 2
                              ? 'bg-error'
                              : passwordStrength?.score <= 4
                              ? 'bg-warning' :'bg-success'
                          }`}
                          style={{ width: `${(passwordStrength?.score / 6) * 100}%` }}
                        />
                      </div>
                      <span className={`text-xs font-medium ${passwordStrength?.color}`}>
                        {passwordStrength?.text}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Use 8+ characters with uppercase, lowercase, numbers, and symbols
                    </p>
                  </div>
                )}
              </div>

              {/* Confirm Password Field */}
              <div className="relative">
                <Input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  id="confirmPassword"
                  value={formData?.confirmPassword}
                  onChange={handleInputChange}
                  placeholder="Re-enter new password"
                  label="Confirm New Password"
                  required
                  disabled={loading || message?.type === 'success'}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-[38px] text-muted-foreground hover:text-foreground transition-colors"
                  disabled={loading || message?.type === 'success'}
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                >
                  <Icon name={showConfirmPassword ? 'EyeOff' : 'Eye'} size={18} />
                </button>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                variant="default"
                size="lg"
                fullWidth
                loading={loading}
                disabled={loading || message?.type === 'success'}
                className="bg-primary hover:bg-primary/90 text-primary-foreground"
              >
                {loading ? 'Updating Password...' : 'Update Password'}
              </Button>

              {/* Back to Login Button */}
              <Button
                type="button"
                variant="outline"
                size="lg"
                fullWidth
                onClick={handleBackToLogin}
                disabled={loading}
                iconName="ArrowLeft"
                iconPosition="left"
                className="border-border hover:bg-muted"
              >
                Back to Login
              </Button>
            </form>

            {/* Security Notice */}
            <div className="mt-8 pt-6 border-t border-border">
              <div className="flex items-start gap-3 text-xs text-muted-foreground">
                <Icon name="Shield" size={16} className="flex-shrink-0 mt-0.5" />
                <p>
                  Your password will be encrypted and stored securely. Make sure to use a strong,
                  unique password that you don't use anywhere else.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ResetPasswordPage;